import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { AlertColor, Box, IconButton, Tooltip, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import BoxContent from "../../../components/boxContent";
import DataGridApi from "../../../components/dataGridApi";
import QuickAlert from "../../../components/form/quickAlert";
import ModalBatchParticipants from "../../../components/loyalty/ModalBatchParticipants";
import ModalBatchErrors from "../../../components/loyalty/ModalBatchErrors";
import ModalLoyaltyBatch from "../../../components/loyalty/ModalLoyaltyBatch";

type BatchStatus = "PROCESSING" | "DONE" | "FAILED";

export default function LoyaltyGroups() {
  const { t } = useTranslation();
  const [dataUpdated, setDataUpdated] = useState(0);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [messageQuickAlert, setMessageQuickAlert] = useState("");
  const [severityAlert, setSeverityAlert] = useState<AlertColor>("success");
  const [titleMessage, setTitleMessage] = useState("");
  const [openUpload, setOpenUpload] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [openParticipants, setOpenParticipants] = useState(false);
  const [openErrors, setOpenErrors] = useState(false);

  const getStatusLabel = (status: BatchStatus, failedRows: number): string => {
    if (status === "PROCESSING") return t("loyalty.groups.status.processing");
    if (status === "DONE" && failedRows === 0)
      return t("loyalty.groups.status.done");
    if (status === "DONE" && failedRows > 0)
      return t("loyalty.groups.status.doneWithErrors");
    return t("loyalty.groups.status.failed");
  };

  const columns: GridColDef[] = [
    {
      field: "fileName",
      headerName: t("loyalty.groups.columns.name"),
      flex: 1.5,
      minWidth: 200,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
      valueGetter: (_value: any, row: any) => row?.groupName,
    },
    {
      field: "createdAt",
      headerName: t("loyalty.groups.columns.importDate"),
      flex: 1,
      minWidth: 160,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (value: string) =>
        value ? dayjs(value).format("DD/MM/YYYY") : "-",
    },
    {
      field: "status",
      headerName: t("loyalty.groups.columns.status"),
      flex: 1,
      minWidth: 180,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
      valueGetter: (_value: any, row: any) =>
        getStatusLabel(row?.status, row?.failedRows ?? 0),
    },
    {
      field: "actions",
      headerName: t("loyalty.groups.columns.actions"),
      flex: 0.7,
      minWidth: 110,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      headerClassName: "header-style",
      renderCell: (params) => {
        const status: BatchStatus = params.row?.status;
        const failedRows: number = params.row?.failedRows ?? 0;
        const isDone = status === "DONE" || status === "PROCESSING";
        const hasErrors = failedRows > 0 || status === "FAILED";

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {isDone && (
              <Tooltip title={t("button.View")}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedRow(params.row);
                    setOpenParticipants(true);
                  }}
                >
                  <RemoveRedEyeOutlinedIcon
                    sx={{ fontSize: 20, color: "var(--blue-400)" }}
                  />
                </IconButton>
              </Tooltip>
            )}
            {hasErrors && (
              <Tooltip title={t("loyalty.groups.errors.title")}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedRow(params.row);
                    setOpenErrors(true);
                  }}
                >
                  <WarningAmberRoundedIcon
                    sx={{ fontSize: 20, color: "var(--blue-400)" }}
                  />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <BoxContent
      title={t("loyalty.groups.pageTitle")}
      button
      titleButton={t("loyalty.groups.uploadButton")}
      handleClick={() => setOpenUpload(true)}
    >
      <QuickAlert
        visibility={visibleAlert}
        description={messageQuickAlert}
        severity={severityAlert}
        title={titleMessage}
        hanhleClose={() => setVisibleAlert(false)}
      />

      <Typography
        sx={{
          fontWeight: 400,
          fontSize: 12,
          lineHeight: "16px",
          letterSpacing: 0,
          marginBottom: 1,
        }}
      >
        {t("loyalty.groups.searchDescription")}
      </Typography>

      <DataGridApi
        endPoint="loyalty/batch?"
        columnsData={columns}
        dataUpdated={dataUpdated}
        onEmptyMessageApi={t("loyalty.groups.emptyState")}
        labelParams1={t("loyalty.groups.searchLabel")}
        globalSearchEnabled
        globalSearchParam="&search="
        cleanButtonText={t("loyalty.groups.cleanButton")}
        cleanButtonType="outlined"
        filterInputWidth={280}
        disableRowSelectionOnClick
      />

      {openUpload && (
        <ModalLoyaltyBatch
          modalChange={setOpenUpload}
          onSuccess={() => {
            setOpenUpload(false);
            setDataUpdated((n) => n + 1);
            setSeverityAlert("success");
            setTitleMessage(t("loyalty.batch.successMessage"));
            setMessageQuickAlert("");
            setVisibleAlert(true);
          }}
          onError={(msg) => {
            setSeverityAlert("error");
            setTitleMessage(msg ?? t("loyalty.batch.uploadError"));
            setMessageQuickAlert("");
            setVisibleAlert(true);
          }}
        />
      )}
      {openParticipants && selectedRow && (
        <ModalBatchParticipants
          jobId={selectedRow.jobId}
          jobLabel={selectedRow.groupName}
          modalChange={setOpenParticipants}
        />
      )}
      {openErrors && selectedRow && (
        <ModalBatchErrors
          jobId={selectedRow.jobId}
          jobLabel={selectedRow.groupName}
          modalChange={setOpenErrors}
        />
      )}
    </BoxContent>
  );
}
